import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import Button from "@mui/material/Button";
import PaymentIcon from "@mui/icons-material/Payment";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import CreditCardIcon   from '@mui/icons-material/CreditCard';

import "./App.css";
import config from "./config";

export default function ViewPayment() {
    const [amount, setAmount] = useState(null);
    const [plate, setPlate] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [clock, setClock] = useState("");
    const [alreadyGotDiscount, setAlreadyGotDiscount] = useState(false);

    const [searchParams] = useSearchParams();
    const navigate     = useNavigate();
    const location     = useLocation();

    const userId  = localStorage.getItem("userIdFromPal");
    const placeId = localStorage.getItem("placeId");
    const discount = Number(location.state?.paymentDiscount || 0);

    // Helper to format stay time string (hrs/mins)
    function formatStayTime(stayTime) {
        let totalHours, minutes;

        if (stayTime.includes('.')) {
            // Case with days
            let [days, time] = stayTime.split('.');
            let [hours, mins] = time.split(':').map(Number);

            totalHours = (parseInt(days) * 24) + hours; // Convert days to hours and add to existing hours
            minutes = mins;
        } else {
            // Case with only hours, minutes, seconds
            let [hours, mins] = stayTime.split(':').map(Number);
            totalHours = hours;
            minutes = mins;
        }

        if (totalHours === 0) {
            return `${minutes} דקות`; // Only minutes if hours are 0
        } else {
            return `${totalHours}:${minutes.toString().padStart(2, '0')} שעות`; // Show as hours:minutes
        }
    }



    // 1) Validate & parse params
    useEffect(() => {
        const guid   = searchParams.get("guid");
        const p      = searchParams.get("plate");
        const amtStr = searchParams.get("amount");
        const from   = location.state?.from;

        if (from !== "/enter-car") {
            setError("גישה לא תקינה. חזרה לדף הראשי…");
            return void setTimeout(() => navigate(`/enter-car?guid=${guid}`), 3000);
        }

        if (!guid || !p || !amtStr || isNaN(+amtStr)) {
            setError("פרטים חסרים. חזרה לדף הראשי…");
            return void setTimeout(() => navigate(`/enter-car?guid=${guid}`), 3000);
        }

        setPlate(p);
        setAmount(+amtStr);
        localStorage.setItem("amountToPay", +amtStr);
    }, [navigate, searchParams, location.state]);

    // 2) Clock ticker
    useEffect(() => {
        const iv = setInterval(() => {
            setClock(new Date().toLocaleTimeString("en-GB"));
        }, 1000);
        return () => clearInterval(iv);
    }, []);

    // 3) Check discount usage
    useEffect(() => {
        if (!userId || !placeId) return;
        setLoading(true);
        fetch(
            `${config.rtc.serverUrl}/is-vpof-user-already-got-discount?` +
            `userId=${encodeURIComponent(userId)}` +
            `&placeId=${encodeURIComponent(placeId)}`
        )
            .then((r) => {
                if (!r.ok) throw new Error(`HTTP ${r.status}`);
                return r.json();
            })
            .then((data) => {
                if (data.success) {
                    setAlreadyGotDiscount(data.alreadyGotDiscount);
                    localStorage.setItem("alreadyGotDiscount", data.alreadyGotDiscount);
                } else {
                    setError(data.message || "Unknown error");
                }
            })
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, [userId, placeId]);

    // 4) Handlers
    const handlePaymentClick = () => {
        // if discount covers full amount…
        if (amount <= discount && !alreadyGotDiscount) {
            localStorage.setItem("amountToPay", amount);
            return navigate("/completion", {
                state: { code: "999999999", plate, deviceguid: searchParams.get("guid") },
            });
        }
        // otherwise go to real payment
        navigate(
            `/payment?guid=${searchParams.get("guid")}&plate=${plate}&amount=${amount}`,
            {
                state: {
                    from: "/view-payment",
                    paymentCompany: location.state.paymentCompany,
                    siteName:       location.state.siteName,
                    paymentDiscount: discount,
                },
            }
        );
    };

    const handleBarcodeClick = () => {
        if (amount === 0) {
            return navigate(`/enter-car?guid=${searchParams.get("guid")}`);
        }
        if (amount <= discount && !alreadyGotDiscount) {
            localStorage.setItem("amountToPay", amount);
            return navigate("/completion", {
                state: { code: "999999999", plate, deviceguid: searchParams.get("guid") },
            });
        }
        navigate(
            `/qr-scanner?guid=${searchParams.get("guid")}&plate=${plate}`,
            { state: { from: "/view-payment", withPayment: location.state?.withPayment,withQr: location?.state?.withQr, paymentCompany: location.state?.paymentCompany, siteName: location.state?.siteName } }
        );
    };

    // 5) Render
    return (
        <div className="payment-container">
            <button className="back-button" onClick={() => navigate(-1)}>
                <img src="/arrow.png" alt="חזור" />
            </button>
                <span style={{marginTop: "5%"}}>
            <svg width="54" height="51" viewBox="0 0 54 51" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                    d="M42.3661 33.2957C42.7059 33.95 42.8947 34.6427 42.8947 35.4125V35.5664C42.8947 38.1066 40.8558 40.2235 38.3262 40.2235H21.0716C18.1644 40.2235 15.7858 42.6482 15.7858 45.6117C15.7858 48.5753 18.1644 51 21.0716 51H38.3262C46.6703 51 53.4286 44.1107 53.4286 35.6049V35.451C53.4286 32.218 52.447 29.216 50.7479 26.7143C48.6336 29.6778 45.7264 31.9871 42.3661 33.3342"
                    fill="#0497CD"/>
                <path
                    d="M5.2937 51C2.38217 51 0 48.6297 0 45.7241V0H38.2659C46.6224 0 53.4286 6.84333 53.4286 15.3306V15.407C53.4286 23.8561 46.6602 30.7376 38.2659 30.7376H23.9729C21.137 30.7376 18.6792 28.5967 18.4901 25.7294C18.3011 22.6327 20.7211 20.0712 23.746 20.0712H38.2281C40.7615 20.0712 42.8034 18.0067 42.8034 15.4453V15.3688C42.8034 12.8073 40.7615 10.7429 38.2281 10.7429H10.5118L10.6252 45.6094C10.6252 48.5532 8.31867 50.9618 5.40714 51C5.40714 51 5.36933 51 5.33151 51"
                    fill="#003C6B"/>
            </svg></span>
            <h1 className="payment-title">פרטי תשלום</h1>

            {error ? (
                <span style={{
                    fontFamily: "Heebo",
                    fontStyle: "normal",
                    maxWidth: "80%", // limit width to prevent overflow
                    fontWeight: 400,
                    fontSize: 18,         // px
                    lineHeight: "27px",
                    textAlign: "center",
                    color: "red",}}>{error}</span>
            ) : loading ? (
                <div className="spinner"/>
            ) : (
                <>
                    <div className="info-section">
                        <div className="info-row">
                            <span>רכב מספר:</span>
                            <span style={{marginLeft: "auto", marginRight: "10px", fontWeight: "700"}}>{plate}</span>
                        </div>
                        <div className="info-row">
                            <span>זמן שהייה:</span>
                            <span  style={{marginLeft: "auto", marginRight: "10px", fontWeight: "700"}}>
    {formatStayTime(searchParams.get("stayTime") || "")}
  </span>
                        </div>
                    </div>

                    <div className="amount-box">
                        <div className="amount-label">הסכום לתשלום:</div>
                        <div className={alreadyGotDiscount ? 'amount-value' : 'amount-discount-used'}>₪ {amount}</div>
                        {!alreadyGotDiscount && discount > 0 && (
                            <div className="amount-value">
                                לתשלום לאחר הנחת אונליין: ₪{Math.max(amount - discount, 0)}
                            </div>
                        )}
                        {alreadyGotDiscount && (
                            <div className="amount-discount-used">
                                הנחת אונליין נוצלה
                            </div>
                        )}
                    </div>

                    <div className="actions">
                        {amount > 0 && (
                            <>
                            {location.state?.withQr && <Button
                                    variant="contained"
                                    className="action-btn qr-btn"
                                    onClick={handleBarcodeClick}
                                    sx={{

                                        fontFamily: 'Heebo',
                                        fontStyle: "normal",
                                        fontWeight: 900,
                                        fontSize: 19,              // px
                                        lineHeight: "28px",        // exact px height
                                        textAlign: "right",

                                        color: "#FFFFFF",

                                        /* Inside auto layout */
                                        flex: "none",              // same as flex: 0 0 auto
                                        order: 0,
                                        flexGrow: 0,
                                    }}
                                    startIcon={
                                    <span style={{marginLeft: "10px"}}><svg width="23" height="22" viewBox="0 0 23 23" fill="none"
                                                    xmlns="http://www.w3.org/2000/svg">
                                        <path
                                            d="M14.95 20.3H18.4C19.01 20.3 19.595 20.0682 20.0263 19.6556C20.4577 19.2431 20.7 18.6835 20.7 18.1V14.8C20.7 14.5083 20.8212 14.2285 21.0368 14.0222C21.2525 13.8159 21.545 13.7 21.85 13.7C22.155 13.7 22.4475 13.8159 22.6632 14.0222C22.8788 14.2285 23 14.5083 23 14.8V18.1C23 19.267 22.5154 20.3861 21.6527 21.2113C20.79 22.0364 19.62 22.5 18.4 22.5H4.6C3.38 22.5 2.20998 22.0364 1.34731 21.2113C0.484642 20.3861 0 19.267 0 18.1V14.8C0 14.5083 0.12116 14.2285 0.336827 14.0222C0.552494 13.8159 0.845001 13.7 1.15 13.7C1.455 13.7 1.74751 13.8159 1.96317 14.0222C2.17884 14.2285 2.3 14.5083 2.3 14.8V18.1C2.3 18.6835 2.54232 19.2431 2.97365 19.6556C3.40499 20.0682 3.99 20.3 4.6 20.3H8.05C8.355 20.3 8.64751 20.4159 8.86317 20.6222C9.07884 20.8285 9.2 21.1083 9.2 21.4C9.2 21.6917 9.07884 21.9715 8.86317 22.1778C8.64751 22.3841 8.355 22.5 8.05 22.5H14.95C14.645 22.5 14.3525 22.3841 14.1368 22.1778C13.9212 21.9715 13.8 21.6917 13.8 21.4C13.8 21.1083 13.9212 20.8285 14.1368 20.6222C14.3525 20.4159 14.645 20.3 14.95 20.3ZM2.3 8.2C2.3 8.49174 2.17884 8.77153 1.96317 8.97782C1.74751 9.18411 1.455 9.3 1.15 9.3C0.845001 9.3 0.552494 9.18411 0.336827 8.97782C0.12116 8.77153 0 8.49174 0 8.2V4.9C0 3.73305 0.484642 2.61389 1.34731 1.78873C2.20998 0.96357 3.38 0.5 4.6 0.5L8.05 0.5C8.355 0.5 8.64751 0.615893 8.86317 0.822183C9.07884 1.02847 9.2 1.30826 9.2 1.6C9.2 1.89174 9.07884 2.17153 8.86317 2.37782C8.64751 2.58411 8.355 2.7 8.05 2.7H4.6C3.99 2.7 3.40499 2.93179 2.97365 3.34437C2.54232 3.75695 2.3 4.31652 2.3 4.9V8.2ZM20.7 8.2V4.9C20.7 4.31652 20.4577 3.75695 20.0263 3.34437C19.595 2.93179 19.01 2.7 18.4 2.7H14.95C14.645 2.7 14.3525 2.58411 14.1368 2.37782C13.9212 2.17153 13.8 1.89174 13.8 1.6C13.8 1.30826 13.9212 1.02847 14.1368 0.822183C14.3525 0.615893 14.645 0.5 14.95 0.5L18.4 0.5C19.62 0.5 20.79 0.96357 21.6527 1.78873C22.5154 2.61389 23 3.73305 23 4.9V8.2C23 8.49174 22.8788 8.77153 22.6632 8.97782C22.4475 9.18411 22.155 9.3 21.85 9.3C21.545 9.3 21.2525 9.18411 21.0368 8.97782C20.8212 8.77153 20.7 8.49174 20.7 8.2Z"
                                            fill="#FFFBFB"/>
                                    </svg>
                                    </span>
                                    }
                                >
                                    לסריקת ברקוד הנחה
                                </Button>}

                                {location.state?.withPayment && <Button
                                    variant="contained"
                                    className="action-btn pay-btn"
                                    sx={{

                                        fontFamily: "Heebo",
                                        fontStyle: "normal",
                                        fontWeight: 900,
                                        fontSize: 19,              // px
                                        lineHeight: "28px",        // exact px height
                                        textAlign: "right",

                                        color: "#FFFFFF",

                                        /* Inside auto layout */
                                        flex: "none",              // same as flex: 0 0 auto
                                        order: 0,
                                        flexGrow: 0,
                                    }}
                                    onClick={handlePaymentClick}
                                    startIcon={<span style={{marginLeft: "10px"}}>
                                <svg width="23" height="17" viewBox="0 0 23 17" fill="none"
                                     xmlns="http://www.w3.org/2000/svg">
<path
    d="M0 14.2955C0 15.0127 0.302901 15.7007 0.842068 16.2079C1.38124 16.7151 2.1125 17 2.875 17H20.125C20.8875 17 21.6188 16.7151 22.1579 16.2079C22.6971 15.7007 23 15.0127 23 14.2955V6.85795H0V14.2955ZM3.38839 10.625C3.38839 10.2407 3.55066 9.87221 3.8395 9.6005C4.12834 9.32878 4.52009 9.17614 4.92857 9.17614H7.39286C7.80134 9.17614 8.19309 9.32878 8.48193 9.6005C8.77077 9.87221 8.93304 10.2407 8.93304 10.625V11.5909C8.93304 11.9752 8.77077 12.3437 8.48193 12.6154C8.19309 12.8871 7.80134 13.0398 7.39286 13.0398H4.92857C4.52009 13.0398 4.12834 12.8871 3.8395 12.6154C3.55066 12.3437 3.38839 11.9752 3.38839 11.5909V10.625ZM20.125 0H2.875C2.1125 0 1.38124 0.284943 0.842068 0.792143C0.302901 1.29934 0 1.98726 0 2.70455V3.96023H23V2.70455C23 1.98726 22.6971 1.29934 22.1579 0.792143C21.6188 0.284943 20.8875 0 20.125 0Z"
    fill="white"/>
</svg>

                                    </span>}
                                >
                                    תשלום באשראי
                                </Button>}
                                {!location.state?.withPayment && !location.state?.withQr &&
                                <div style={{
                                    fontFamily: "Heebo",
                                    fontStyle: "normal",
                                    fontWeight: 900,
                                    fontSize: 19,              // px
                                    lineHeight: "28px",        // exact px height
                                    textAlign: "right",
                                    color: "#FF0000", // red color for warning
                                    /* Inside auto layout */
                                    flex: "none",              // same as flex: 0 0 auto
                                    order: 0,
                                    flexGrow: 0,

                                }}>
                              יש לשלם ביציאה או בעמדת התשלום
                                </div>
                                }
                            </>
                        )}

                        {amount === 0 && (
                            <Button
                                variant="contained"
                                className="action-btn full-btn"
                                onClick={handleBarcodeClick}
                            >
                                חזרה לדף הראשי
                            </Button>
                        )}
                    </div>
                </>
            )}

            <footer className="footer">
                <div className="footer-content">
                    <p>
                        מספר רכב נוכחי: <span>{plate}</span>
                    </p>
                    <p>
                        שעה נוכחית: <span>{clock}</span>
                    </p>
                    <p>© BluePT {new Date().getFullYear()}</p>
                </div>
            </footer>
        </div>
    );
}
